import type { MetaAdDraftInput } from '../../types/metaAdDraft';
import { defaultOptimizationGoal, validOptimizationGoals } from '../../lib/metaAdObjectiveShape';

// Changing the campaign destination type must keep ads.destination.type in lock-
// step (validateMetaAdDraft requires them to agree). Pure — preserves any URL /
// lead-form already entered for the matching destination type. Used by the
// destination radio AND CampaignSection's objective→traffic flip.
//
// Multi-ad-set: destinationType / optimizationGoal / customEventType are now
// campaign-wide-locked (shared by every ad set), and the creative/destination is
// the shared `ads`. D2.17 — also re-derives the campaign optimizationGoal so it
// stays valid for the new destination (+ the current objective): a still-valid
// value is kept; an invalid one falls back to the default. customEventType is
// cleared unless the resulting goal is OFFSITE_CONVERSIONS.
export function applyDestinationType(
  input: MetaAdDraftInput,
  destinationType: 'instant_form' | 'website',
): Pick<MetaAdDraftInput, 'campaign' | 'ads'> {
  const ad = input.ads;
  const destination =
    destinationType === 'website'
      ? { type: 'website' as const, url: ad.destination.type === 'website' ? ad.destination.url : '' }
      : {
          type: 'instant_form' as const,
          leadGenFormId: ad.destination.type === 'instant_form' ? ad.destination.leadGenFormId : '',
          leadGenFormName: ad.destination.type === 'instant_form' ? ad.destination.leadGenFormName : '',
        };
  const objective = input.campaign.objective;
  const optimizationGoal = validOptimizationGoals(objective, destinationType).includes(input.campaign.optimizationGoal)
    ? input.campaign.optimizationGoal
    : defaultOptimizationGoal(objective, destinationType);
  const customEventType = optimizationGoal === 'OFFSITE_CONVERSIONS' ? input.campaign.customEventType : null;
  return {
    campaign: { ...input.campaign, destinationType, optimizationGoal, customEventType },
    ads: { ...ad, destination },
  };
}
